/**
 * MediaSelector Component
 *
 * Compact modal for selecting media from library
 * Supports multi-select, tabs, server-side search, and infinite scroll
 * Used in campaign creation and other contexts
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { apiClient } from '@/services/api';
import { useToast } from '@/components/ui/Toast';
import { X, Image as ImageIcon, Video, Search, UploadCloud, Loader2 } from 'lucide-react';
import { DeskButton } from '@/components/growth-desk/ui';
import { MediaAsset } from '@/hooks/useMediaUpload';
import { MediaGroup, MediaGroupTabs, sourceLabel } from './mediaGroups';

interface MediaSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (assets: MediaAsset[]) => void;
  multiSelect?: boolean;
  mediaType?: 'image' | 'video' | 'all';
  maxSelection?: number;
  /** Tab to open on: 'uploads' where the user is picking their own product photos (Ad Studio,
   * campaign chat). Falls back to 'all' when there are no uploads yet. */
  defaultGroup?: MediaGroup;
}

export const MediaSelector: React.FC<MediaSelectorProps> = ({
  isOpen,
  onClose,
  onSelect,
  multiSelect = true,
  mediaType = 'all',
  maxSelection,
  defaultGroup = 'all',
}) => {
  const { toast } = useToast();
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  // Selected assets themselves (not just ids), so a selection survives switching tabs,
  // searching, or scrolling — the grid only holds what's loaded for the current view.
  const [selected, setSelected] = useState<Map<string, MediaAsset>>(new Map());
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<string>(mediaType === 'all' ? '' : mediaType);
  const [group, setGroup] = useState<MediaGroup>(defaultGroup);
  const [groupCounts, setGroupCounts] = useState<Partial<Record<MediaGroup, number>> | null>(null);
  const [fellBack, setFellBack] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mode, setMode] = useState<'library' | 'upload' | 'link'>('library');
  const [linkUrl, setLinkUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Ignores responses for a view the user has already left (tab/search changed mid-request).
  const requestRef = useRef(0);

  const pageSize = 24;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const loadPage = useCallback(async (pageNum: number) => {
    const req = ++requestRef.current;
    if (pageNum === 1) setLoading(true); else setLoadingMore(true);
    try {
      const params = new URLSearchParams({ page: pageNum.toString(), page_size: pageSize.toString() });
      if (filter) params.append('media_type', filter);
      if (group !== 'all') params.append('group', group);
      if (debouncedSearch) params.append('search', debouncedSearch);

      const response = await apiClient.get(`/api/v1/media?${params.toString()}`);
      if (req !== requestRef.current) return;
      const counts = response.data.group_counts as Partial<Record<MediaGroup, number>> | undefined;
      if (counts) setGroupCounts(counts);
      // Opening on 'uploads' with none yet would show an empty grid — show everything instead.
      if (pageNum === 1 && group !== 'all' && group === defaultGroup && !fellBack && !debouncedSearch
          && counts && !counts[group]) {
        setFellBack(true);
        setGroup('all');
        return;
      }
      const items: MediaAsset[] = response.data.items;
      const totalCount: number = response.data.total;
      setAssets(prev => pageNum === 1 ? items : [...prev, ...items]);
      setTotal(totalCount);
      setHasMore(pageNum * pageSize < totalCount);
    } catch (error) {
      console.error('Failed to load media:', error);
    } finally {
      if (req === requestRef.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [filter, group, debouncedSearch, defaultGroup, fellBack]);

  // A new view (opened, tab, type, search) starts again from page 1 at the top.
  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    setHasMore(false);
    scrollRef.current?.scrollTo({ top: 0 });
    loadPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filter, group, debouncedSearch]);

  // Next page when the sentinel at the bottom of the grid scrolls into view.
  useEffect(() => {
    if (!isOpen || !hasMore || loading || loadingMore || !sentinelRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        const next = page + 1;
        setPage(next);
        loadPage(next);
      }
    }, { root: scrollRef.current, rootMargin: '200px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [isOpen, hasMore, loading, loadingMore, page, loadPage]);

  const handleAssetClick = (asset: MediaAsset) => {
    if (multiSelect) {
      const next = new Map(selected);
      if (next.has(asset.id)) {
        next.delete(asset.id);
      } else {
        if (maxSelection && next.size >= maxSelection) {
          toast.warning(`You can only select up to ${maxSelection} items`);
          return;
        }
        next.set(asset.id, asset);
      }
      setSelected(next);
    } else {
      // Single select - immediately select and close
      onSelect([asset]);
      handleClose();
    }
  };

  const handleConfirm = () => {
    onSelect(Array.from(selected.values()));
    handleClose();
  };

  const handleClose = () => {
    setSelected(new Map());
    setAssets([]);
    setPage(1);
    setFilter(mediaType === 'all' ? '' : mediaType);
    setGroup(defaultGroup);
    setFellBack(false);
    setSearchQuery('');
    setDebouncedSearch('');
    setMode('library');
    setLinkUrl('');
    onClose();
  };

  if (!isOpen) return null;

  const noun = mediaType === 'image' ? 'photo' : mediaType === 'video' ? 'video' : 'file';
  const order = Array.from(selected.keys());
  const ratio = (a: MediaAsset) => {
    if (!a.width || !a.height) return null;
    const r = a.width / a.height;
    const hit = ([[1, '1:1'], [4 / 5, '4:5'], [9 / 16, '9:16'], [16 / 9, '16:9'], [1.91, '1.91:1']] as [number, string][]).find(([v]) => Math.abs(v - r) < 0.03);
    return hit ? hit[1] : `${a.width}×${a.height}`;
  };
  // Upload / From a link land in the library, then get picked like any other file.
  const added = (asset: MediaAsset | undefined) => {
    setMode('library');
    if (asset) {
      if (!multiSelect) { onSelect([asset]); handleClose(); return; }
      setSelected(prev => (maxSelection && prev.size >= maxSelection) ? prev : new Map(prev).set(asset.id, asset));
    }
    setGroup('all'); setPage(1); loadPage(1);
  };
  const upload = async (file: File) => {
    setBusy(true);
    try {
      const form = new FormData(); form.append('file', file);
      const { data } = await apiClient.post('/api/v1/media/upload', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      added(data?.asset);
    } catch (e: any) { toast.error(e?.response?.data?.detail || e?.message || 'Upload failed'); }
    finally { setBusy(false); }
  };
  const importLink = async () => {
    if (!linkUrl.trim()) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post('/api/v1/media/import-url', { url: linkUrl.trim() }, { timeout: 60000 });
      setLinkUrl(''); added(data?.asset);
    } catch (e: any) { toast.error(e?.response?.data?.detail || e?.message || 'Couldn’t import that link'); }
    finally { setBusy(false); }
  };
  const MODES = [{ id: 'library', label: 'Library' }, { id: 'upload', label: 'Upload' }, { id: 'link', label: 'From a link' }] as const;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto bg-navy/60 p-4" onClick={handleClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="media-picker-title" onClick={e => e.stopPropagation()}
        className="my-8 flex max-h-[90vh] w-full max-w-5xl flex-col rounded-[28px] bg-card shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-7 pt-6">
          <div className="flex flex-col gap-1.5">
            <span className="text-small text-ink-2">
              {multiSelect ? `Pick up to ${maxSelection ?? 'any number of'} ${noun}${maxSelection === 1 ? '' : 's'}` : `Pick a ${noun}`}
            </span>
            <h2 id="media-picker-title" className="m-0 font-display text-[30px] font-extrabold leading-none tracking-[-0.03em] text-ink">
              {mediaType === 'image' ? (multiSelect ? 'Choose photos' : 'Choose a photo') : mediaType === 'video' ? 'Choose a video' : 'Choose media'}
            </h2>
          </div>
          <button type="button" onClick={handleClose} aria-label="Close"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-btn bg-inset text-ink hover:bg-line">
            <X size={18} />
          </button>
        </div>

        {/* Library / Upload / From a link, and the filters */}
        <div className="mx-7 mt-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-2 border-b border-line">
          <nav aria-label="Source" className="-mb-px flex gap-6">
            {MODES.map(m => (
              <button key={m.id} type="button" onClick={() => setMode(m.id)} aria-pressed={mode === m.id}
                className={`inline-flex min-h-[44px] items-center border-b-[3px] font-display text-[15px] font-bold transition-colors ${mode === m.id ? 'border-brand text-ink' : 'border-transparent text-ink-3 hover:text-ink'}`}>
                {m.label}
              </button>
            ))}
          </nav>
          {mode === 'library' && <MediaGroupTabs value={group} onChange={setGroup} counts={groupCounts} className="pb-2" />}
        </div>

        {mode === 'library' && (
          <div className="mx-7 mt-3 flex flex-wrap items-center gap-2">
            <label className="relative min-w-[200px] flex-1">
              <span className="sr-only">Search media</span>
              <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-3" aria-hidden="true" />
              <input type="search" placeholder="Search by name" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="min-h-[40px] w-full rounded-full border-0 bg-ground pl-9 pr-4 text-sm text-ink shadow-ring-line placeholder:text-ink-3 focus:outline-none focus:shadow-ring-ink" />
            </label>
            {mediaType === 'all' && (
              <select value={filter} onChange={e => setFilter(e.target.value)} aria-label="Type"
                className="min-h-[40px] rounded-full border-0 bg-ground px-4 text-sm font-semibold text-ink shadow-ring-line focus:outline-none">
                <option value="">Images &amp; videos</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
              </select>
            )}
            <span className="font-money text-xs text-ink-3">{total}</span>
          </div>
        )}

        {/* Body */}
        <div ref={scrollRef} className="min-h-[240px] flex-1 overflow-y-auto px-7 py-5">
          {mode === 'upload' ? (
            <label className={`flex min-h-[220px] cursor-pointer flex-col items-center justify-center gap-2 rounded-panel border-2 border-dashed border-line-2 text-center transition-colors hover:border-ink ${busy ? 'pointer-events-none opacity-60' : ''}`}>
              <UploadCloud className="h-7 w-7 text-ink-2" aria-hidden="true" />
              <span className="font-display text-card-title font-bold text-ink">{busy ? 'Uploading…' : `Choose a ${noun} from your device`}</span>
              <span className="text-small text-ink-2">It goes into your Media Library too.</span>
              <input type="file" className="sr-only" accept={mediaType === 'image' ? 'image/*' : mediaType === 'video' ? 'video/*' : 'image/*,video/*'}
                onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
            </label>
          ) : mode === 'link' ? (
            <form className="flex flex-col gap-3" onSubmit={e => { e.preventDefault(); importLink(); }}>
              <label htmlFor="media-picker-link" className="text-small text-ink-2">A direct link to an image. It goes into your Media Library too.</label>
              <div className="flex gap-2">
                <input id="media-picker-link" type="url" required autoFocus value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://…"
                  className="min-h-[48px] flex-1 rounded-btn border-0 bg-ground px-3.5 text-sm text-ink shadow-ring-line focus:outline-none focus:shadow-ring-ink" />
                <DeskButton type="submit" variant="navy" loading={busy}>Import</DeskButton>
              </div>
            </form>
          ) : loading ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
              {Array.from({ length: 5 }).map((_, i) => <div key={i} className="aspect-square animate-pulse rounded-card bg-inset" />)}
            </div>
          ) : assets.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <ImageIcon size={36} className="text-ink-3" aria-hidden="true" />
              <p className="m-0 font-display text-card-title font-bold text-ink">Nothing here</p>
              <p className="m-0 text-small text-ink-2">{debouncedSearch || group !== 'all' ? 'Try another filter or search.' : 'Upload one, or import it from a link.'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 md:grid-cols-5">
              {assets.map(asset => {
                const n = order.indexOf(asset.id) + 1;
                const on = n > 0;
                return (
                  <button key={asset.id} type="button" onClick={() => handleAssetClick(asset)} aria-pressed={multiSelect ? on : undefined}
                    className="group flex min-w-0 flex-col gap-2 text-left">
                    <span className={`relative block aspect-square w-full overflow-hidden rounded-card bg-inset transition-shadow ${on ? 'shadow-[0_0_0_3px_rgb(var(--c-brand))]' : 'group-hover:shadow-ring-ink'}`}>
                      {asset.media_type === 'image'
                        ? <img src={asset.public_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                        : <span className="grid h-full w-full place-items-center text-ink-3"><Video size={30} aria-hidden="true" /></span>}
                      {multiSelect && (
                        <span className={`absolute right-2.5 top-2.5 grid h-7 w-7 place-items-center rounded-full text-sm font-bold ${on ? 'bg-brand text-brand-on' : 'bg-card/80 shadow-ring-line'}`}>
                          {on ? n : ''}
                        </span>
                      )}
                    </span>
                    <span className="truncate text-sm font-semibold text-ink">{asset.original_filename || asset.filename}</span>
                    <span className="-mt-1.5 truncate text-xs text-ink-2">{[ratio(asset), sourceLabel(asset.source)].filter(Boolean).join(' · ')}</span>
                  </button>
                );
              })}
            </div>
          )}
          {/* Infinite scroll: the next page loads as this nears the bottom of the grid */}
          {mode === 'library' && !loading && hasMore && <div ref={sentinelRef} className="h-4" />}
          {mode === 'library' && loadingMore && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-ink-3" /></div>}
        </div>

        {/* Footer */}
        <div className="mx-7 flex flex-wrap items-center justify-between gap-3 border-t border-line py-4">
          <span className="text-small text-ink-2">
            {multiSelect ? `${selected.size} selected${maxSelection ? ` · up to ${maxSelection}` : ''}` : `Click a ${noun} to use it`}
          </span>
          <div className="flex gap-2">
            <DeskButton onClick={handleClose}>Cancel</DeskButton>
            {multiSelect && (
              <DeskButton variant="primary" onClick={handleConfirm} disabled={selected.size === 0}>
                Use {selected.size || ''} {noun}{selected.size === 1 ? '' : 's'}
              </DeskButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal at document body level using portal
  return ReactDOM.createPortal(modalContent, document.body);
};
